import { Button } from "@clarityos/ui/button";
import { Check, MessageCircle } from "lucide-react";
import { Patient } from "../../types";
import { useCallback } from "react";
import { useUpdateCaptureCache } from "../../hooks/useUpdateCaptureCache";
import useSMSSentButtonFeedback from "./useSMSSentButtonFeedback";
import { AnalyticsEvent, trackEvent } from "@/lib/analytics";
import { useInitiateCaptureLink } from "@/features/card-capture/staff/hooks/useInitiateCaptureLink";
import isAdult from "../../utils/isAdult";

interface Props {
  className?: string;
  surface: "HomeScreen" | "SideModal";
  patient: Pick<
    Patient,
    "practiceId" | "patientId" | "cardCapture" | "phone" | "dateOfBirth"
  >;
}

const RequestCardViaSMSBlock = ({ className, surface, patient }: Props) => {
  const { applySentFeedback, clearSentFeedback, sentFeedbackApplied } =
    useSMSSentButtonFeedback();
  const { initiate, isLoading: isInitiating } = useInitiateCaptureLink(
    patient?.practiceId ?? "",
    patient?.patientId ?? "",
  );

  const updateCache = useUpdateCaptureCache(patient?.patientId ?? "");

  const handleSubmit = useCallback(async () => {
    const response = await initiate();
    clearSentFeedback();

    trackEvent(AnalyticsEvent.SMSCardCapture, {
      surface,
      patient_id: patient.patientId,
    });

    updateCache(response);
    applySentFeedback();
  }, [
    initiate,
    applySentFeedback,
    clearSentFeedback,
    updateCache,
    surface,
    patient.patientId,
  ]);

  if (!patient.phone || !isAdult(patient.dateOfBirth)) return null;

  return (
    <div>
      {sentFeedbackApplied ? (
        <Button
          size="sm"
          loading={isInitiating}
          className={className}
          onClick={handleSubmit}
          startIcon={({ className }) => (
            <Check className={className} strokeWidth={2.5} />
          )}
        >
          SMS Sent
        </Button>
      ) : (
        <Button
          size="sm"
          loading={isInitiating}
          className={className}
          onClick={handleSubmit}
          startIcon={({ className }) => (
            <MessageCircle className={className} strokeWidth={2.5} />
          )}
        >
          Request via SMS
        </Button>
      )}
    </div>
  );
};

export default RequestCardViaSMSBlock;
